import { FC } from 'react';
import {
  Button,
  DateInput,
  Modal,
  NumberInput,
  SwitchInput,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from 'store/files';
import { ROUTES } from 'config';
import i18n from 'i18n';
import {
  NewsSendMethod,
  useCreateNew,
  useRemoveNew,
  useUpdateNew,
  loaders as newsLoaders,
} from 'store/news';
import { Translations } from 'types/Common';
import { useCompanyLanguages } from 'state/Administrators';
import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IRecognitionRecipient } from 'types/income';
import { useLoading } from 'utils/Loading';
import { usePointsLabel } from 'store/recognitions';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { routerHistory } from 'utils/router-history';
import { ModalProps } from 'store/modal';
import { ContentLanguage } from 'types/general';
import topRecipientsImagePath from './assets/top-recipients.jpg';

interface Values {
  topCount: number;
  displayPoints: boolean;
  displayCount: boolean;
  from: string | null;
  to: string | null;
}

const INITIAL_VALUES: Values = {
  topCount: 10,
  displayPoints: true,
  displayCount: true,
  from: null,
  to: null,
};

type QuickRangeOptionType = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear';
interface QuickRangeOptionDefinition {
  labelKey: string;
  getValues: () => {
    from: Moment;
    to: Moment;
  };
}

const quickRangeOptionDefinitions: Record<
  QuickRangeOptionType,
  QuickRangeOptionDefinition
> = {
  thisWeek: {
    labelKey: 'common.dateRangeQuickFilters-thisWeek',
    getValues: () => ({
      from: moment().startOf('week'),
      to: moment().endOf('week'),
    }),
  },
  thisMonth: {
    labelKey: 'common.dateRangeQuickFilters-thisMonth',
    getValues: () => ({
      from: moment().startOf('month'),
      to: moment().endOf('month'),
    }),
  },
  lastMonth: {
    labelKey: 'common.dateRangeQuickFilters-lastMonth',
    getValues: () => ({
      from: moment().subtract(1, 'month').startOf('month'),
      to: moment().subtract(1, 'month').endOf('month'),
    }),
  },
  thisYear: {
    labelKey: 'common.dateRangeQuickFilters-thisYear',
    getValues: () => ({
      from: moment().startOf('year'),
      to: moment().endOf('year'),
    }),
  },
};

const convertToQuickRangeOptionType = (from: string, to: string) => {
  if (!from || !to) {
    return;
  }

  const fromDate = moment(from);
  const toDate = moment(to);
  const options = Object.keys(
    quickRangeOptionDefinitions,
  ) as QuickRangeOptionType[];

  return options.find((type) => {
    const values = quickRangeOptionDefinitions[type].getValues();
    return fromDate.isSame(values.from) && toDate.isSame(values.to);
  });
};

interface TopRecipient {
  name: string;
  points: number;
  count: number;
}

interface RequestPeriod {
  from: string;
  to: string;
}

interface TopRecipientsNewsParams {
  period: RequestPeriod;
  topRecipients: TopRecipient[];
  imageId: string;
  language: Uppercase<ContentLanguage>;
  pointsLabel: string;
  displayPoints: boolean;
  displayCount: boolean;
}

interface Update {
  name: string;
  sendMethods: NewsSendMethod[];
  translations: Translations<{
    title: string;
    content: string;
    headerImageId: string | null;
    attachmentIds: string[];
  }>;
}

class TopRecipientsNews implements Update {
  name;
  sendMethods = ['email' as const, 'appNotification' as const];
  translations;

  constructor({
    period: { from, to },
    language,
    topRecipients,
    ...params
  }: TopRecipientsNewsParams) {
    const period = {
      from: moment(from).format('l'),
      to: moment(to).format('l'),
    };

    const translateWithLangContext = (key: string, values?: object) =>
      i18n.t(key, { ...values, lng: language.toLowerCase() });

    this.name = translateWithLangContext(
      'recognition.topRecipientsNewsSystematicName',
      {
        interpolation: { escapeValue: false },
        ...period,
      },
    );

    const rangeOption = convertToQuickRangeOptionType(from, to);

    this.translations = [
      {
        language,
        title: translateWithLangContext('recognition.topRecipientsNewsTitle', {
          count: topRecipients.length,
        }),
        attachmentIds: [],
        content: translateWithLangContext(
          'recognition.topRecipientsNewsContent',
          {
            interpolation: { escapeValue: false },
            periodType: rangeOption,
            ...period,
            topRecipients: `<ol>${topRecipients
              .map((topRecipient) => {
                const getAdditionalInfoText = () => {
                  if (!params.displayPoints && !params.displayCount) {
                    return '';
                  }

                  const pointsText = params.displayPoints
                    ? `${topRecipient.points} ${params.pointsLabel}`
                    : '';
                  const countText = params.displayCount
                    ? translateWithLangContext(
                        'recognition.topRecipientsRecognizedTimes',
                        { count: topRecipient.count },
                      )
                    : '';

                  return ` (${[pointsText, countText]
                    .filter((text) => !!text.length)
                    .join(', ')})`;
                };

                return `<li>${
                  topRecipient.name
                }${getAdditionalInfoText()}</li>`;
              })
              .join('')}</ol>`,
          },
        ),
        headerImageId: params.imageId,
      },
    ];
  }
}

const useTopRecipientsNewGenerator = () => {
  const requestState = useLoading();
  const { defaultLanguage } = useCompanyLanguages();
  const pointsLabel = usePointsLabel();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: createNew } = useCreateNew();
  const { mutateAsync: updateNew } = useUpdateNew();
  const { mutateAsync: removeNew } = useRemoveNew();

  const generate = async (params: Values) => {
    let createdNewsId: string = '';
    requestState.startLoading();

    try {
      // Upload image
      const imageResponse = await fetch(topRecipientsImagePath);
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], 'top-recipients.jpeg');
      const { data } = await uploadFile({ file });

      // Load top recipients
      const recognitionRecipients = await api<{
        employees: IRecognitionRecipient[];
      }>({
        url: Endpoints.clientAdmin.recognitions.recipients.root,
        params: {
          page: 1,
          pageSize: params.topCount,
          sort: '-points',
          filter: {
            dateFrom: params.from,
            dateTo: params.to,
          },
        },
      });
      const topRecipients = recognitionRecipients.data.employees
        .filter((employee) => employee.points > 0)
        .map(({ points, count, ...employee }) => ({
          name: [employee.firstName, employee.lastName]
            .filter(Boolean)
            .join(' '),
          points,
          count,
        }));

      // Generate news data
      const newsData = new TopRecipientsNews({
        period: { ...params, from: params.from ?? '', to: params.to ?? '' },
        topRecipients,
        imageId: data.id,
        language: defaultLanguage,
        pointsLabel,
        displayPoints: params.displayPoints,
        displayCount: params.displayCount,
      });

      // Create news
      const { data: createdNew } = await createNew({
        name: newsData.name,
      });
      createdNewsId = createdNew.id;

      // Populate generated news data to created news
      const { data: updatedNew } = await updateNew({
        newId: createdNewsId,
        update: {
          name: newsData.name,
          sendMethods: newsData.sendMethods,
          translations: newsData.translations,
        },
      });

      // Set all employees as recipients
      const { data: recipients } = await newsLoaders.getRecipients({
        newId: createdNewsId,
        filters: {},
      });

      await newsLoaders.assignRecipients({
        newId: createdNewsId,
        employeeGroupIds: [recipients.data[0].id],
        status: 'on',
      });

      return updatedNew;
    } catch (e) {
      if (createdNewsId) {
        removeNew({ newId: createdNewsId });
      }
      throw e;
    } finally {
      requestState.stopLoading();
    }
  };

  return { generate, loading: requestState.loading };
};

interface Props extends ModalProps {}

export const GenerateTopRecipientsNewsModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const pointsLabel = usePointsLabel();

  const { generate, loading } = useTopRecipientsNewGenerator();

  const { control, handleSubmit, setValue, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const onSubmit: SubmitHandler<Values> = async (values) => {
    const response = await generate(values);
    closeModal();
    routerHistory.push(
      `${ROUTES.news.details.replace(':id', response.id)}?guided=true`,
    );
  };

  const quickRangeOptionTypes: QuickRangeOptionType[] = [
    'thisWeek',
    'thisMonth',
    'lastMonth',
    'thisYear',
  ];
  const quickRangeOptions =
    quickRangeOptionTypes.map((type) => ({
      type,
      ...quickRangeOptionDefinitions[type],
    })) ?? [];

  const quickOptionHandler = (type: QuickRangeOptionType) => () => {
    const values = quickRangeOptionDefinitions[type].getValues();
    setValue('from', values.from.toISOString(true));
    setValue('to', values.to.toISOString(true));
  };

  const [from, to] = watch(['from', 'to']);

  const isActive = (type: QuickRangeOptionType) => {
    if (!from || !to) return false;

    const currentType = convertToQuickRangeOptionType(from, to);
    return type === currentType;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('recognition.generateTopRecipientsNews')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            loading,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="topCount"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('recognition.topRecipientsCount')}
                infoMessage={t('recognition.topRecipientsCountHint')}
                name={name}
                value={value}
                onChange={onChange}
                required
                type="integer"
                key={value}
                ref={ref}
              />
            )}
          />

          <Controller
            name="displayPoints"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SwitchInput
                label={t('recognition.topRecipientsDisplayPoints', {
                  pointsLabel,
                })}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="displayCount"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SwitchInput
                label={t('recognition.topRecipientsDisplayCount')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Stack flexDirection="row" gap={2}>
            <Controller
              name="from"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <DateInput
                  label={t('common.from')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              )}
            />

            <Divider orientation="vertical" variant="middle" flexItem />

            <Controller
              name="to"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <DateInput
                  label={t('common.to')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              )}
            />
          </Stack>

          <Stack direction="row" gap="10px">
            {quickRangeOptions.map(({ type, ...quickOption }) => (
              <Button
                label={t(quickOption.labelKey)}
                key={type}
                onClick={quickOptionHandler(type)}
                variant={isActive(type) ? 'secondary' : 'neutral-outline'}
              />
            ))}
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
